import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
import { makeStyles } from '@material-ui/core';
import FormLabel from '@material-ui/core/FormLabel';
import {
  ATTRIBUTES,
  PRODUCTS_LIST_METHODS,
  PRODUCTS_LIST_LABELS,
  PRODUCTS_LIST_VALUES
} from '../constants';
import { decodeFormikValues } from '../helpers';

import RadioGroupField from './RadioGroupField';
import InfoDialog from '../../InfoDialog';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    marginLeft: '16px'
  },
  verticalLine: {
    borderLeft: '1px solid #e0e0e0',
    position: 'absolute',
    top: '0',
    height: '86%',
    left: '-16px'
  },
  checkboxGroupContainer: {
    display: 'inline-block',
    verticalAlign: 'top',
    marginRight: '16px'
  },
  checkboxGroupContainer2: {
    display: 'inline-block',
    verticalAlign: 'top',
    marginRight: '16px'
  },
  separator: {
    display: 'inline-block',
    width: '1px',
    height: '100%',
    marginRight: '16px',
    backgroundColor: '#ccc'
  },
  appendLabel: {
    fontSize: '12px'
  },
  popupTitle: {
    '& > h2': {
      fontSize: '1rem',
      color: 'red'
    }
  }
}));
const {
  UPPER_SPLINT,
  LOWER_SPLINT,
  OWEN_TRIPOID_SPLINT,
  CUSTOM_SPLINT,
  REGULAR_SPLINT_REPRINT,
  OWEN_TRIPOD_SPLINT_PLUS_BACKUP,
  REGULAR_SPLINT_PLUS_BACKUP,
  OWEN_TRIPOD_REPRINT
} = PRODUCTS_LIST_METHODS;

const ProductsListField = ({
  currentImpression,
  currentValue,
  setValue,
  preferenceValue,
  isSubmitting,
  ...props
}) => {
  const classes = useStyles();
  const [fieldLoaded, setFieldLoaded] = useState(false);
  const [owenSplintInfoShown, setOwenSplintInfoShown] = useState(false);

  useEffect(() => {
    setFieldLoaded(true);
  }, []);

  useEffect(() => {
    const owenSelected =
      (currentValue & PRODUCTS_LIST_VALUES[OWEN_TRIPOID_SPLINT]) > 0 ||
      (currentValue & PRODUCTS_LIST_VALUES[OWEN_TRIPOD_SPLINT_PLUS_BACKUP]) > 0;
    if (fieldLoaded && owenSelected) {
      setOwenSplintInfoShown(true);
    }
  }, [currentValue, fieldLoaded]);

  const owenSplintDialogContent = (
    <div>
      <p>
        The Owen Tripod Splint is designed to require little to no adjustment. However, if your
        patient experiences discomfort, please watch this brief video from Dr. Owen for fitting
        recommendations and adjustment tips.
      </p>
      <a
        href="https://drive.google.com/file/d/1r16yAj5o-l8s3YKLlGJNwseY_7wPqeI8/view?usp=sharing"
        target="_blank"
        rel="noopener noreferrer"
      >
        Watch the fitting guide video
      </a>
    </div>
  );

  const optionsStandardSplints = useMemo(
    () => [
      {
        label: PRODUCTS_LIST_LABELS[UPPER_SPLINT],
        value: PRODUCTS_LIST_VALUES[UPPER_SPLINT],
        checked: (currentValue & PRODUCTS_LIST_VALUES[UPPER_SPLINT]) > 0
      },
      {
        label: PRODUCTS_LIST_LABELS[LOWER_SPLINT],
        value: PRODUCTS_LIST_VALUES[LOWER_SPLINT],
        checked: (currentValue & PRODUCTS_LIST_VALUES[LOWER_SPLINT]) > 0
      },
      {
        label: PRODUCTS_LIST_LABELS[REGULAR_SPLINT_PLUS_BACKUP],
        value: PRODUCTS_LIST_VALUES[REGULAR_SPLINT_PLUS_BACKUP],
        checked: (currentValue & PRODUCTS_LIST_VALUES[REGULAR_SPLINT_PLUS_BACKUP]) > 0
      },
      {
        label: PRODUCTS_LIST_LABELS[REGULAR_SPLINT_REPRINT],
        value: PRODUCTS_LIST_VALUES[REGULAR_SPLINT_REPRINT],
        checked: (currentValue & PRODUCTS_LIST_VALUES[REGULAR_SPLINT_REPRINT]) > 0
      }
    ],
    [currentValue]
  );

  const optionsCustomSplints = useMemo(
    () => [
      {
        label: PRODUCTS_LIST_LABELS[OWEN_TRIPOID_SPLINT],
        value: PRODUCTS_LIST_VALUES[OWEN_TRIPOID_SPLINT],
        checked: (currentValue & PRODUCTS_LIST_VALUES[OWEN_TRIPOID_SPLINT]) > 0
      },
      {
        label: PRODUCTS_LIST_LABELS[OWEN_TRIPOD_SPLINT_PLUS_BACKUP],
        value: PRODUCTS_LIST_VALUES[OWEN_TRIPOD_SPLINT_PLUS_BACKUP],
        checked: (currentValue & PRODUCTS_LIST_VALUES[OWEN_TRIPOD_SPLINT_PLUS_BACKUP]) > 0
      },
      {
        label: PRODUCTS_LIST_LABELS[OWEN_TRIPOD_REPRINT],
        value: PRODUCTS_LIST_VALUES[OWEN_TRIPOD_REPRINT],
        checked: (currentValue & PRODUCTS_LIST_VALUES[OWEN_TRIPOD_REPRINT]) > 0
      },
      {
        label: 'Coming Soon: Design your own custom splint',
        value: PRODUCTS_LIST_VALUES[CUSTOM_SPLINT],
        checked: (currentValue & PRODUCTS_LIST_VALUES[CUSTOM_SPLINT]) > 0,
        disabled: true
      }
    ],
    [currentValue]
  );

  return (
    <div className={classes.container}>
      <InfoDialog
        open={owenSplintInfoShown}
        onClose={() => setOwenSplintInfoShown(false)}
        title="IMPORTANT INFORMATION FOR FITTING THE OWEN TRIPOD SPLINT"
        content={owenSplintDialogContent}
        titleClass={classes.popupTitle}
      />
      <div className={classes.verticalLine} />
      <FormLabel component="legend">Select products: *</FormLabel>
      <div className={classes.checkboxGroupContainer}>
        <div className={classes.checkboxGroupContainer2}>
          <RadioGroupField
            options={optionsStandardSplints}
            name={ATTRIBUTES.PRODUCTS_LIST_METHOD}
            {...props}
            fieldProps={{ row: false }}
            isSubmitting={isSubmitting}
            partial
            appendLabel={<span className={classes.appendLabel}>Standard Splints</span>}
          />
        </div>
        <div className={classes.checkboxGroupContainer2}>
          <RadioGroupField
            options={optionsCustomSplints}
            name={ATTRIBUTES.PRODUCTS_LIST_METHOD}
            {...props}
            fieldProps={{ row: false }}
            isSubmitting={isSubmitting}
            partial
            appendLabel={<span className={classes.appendLabel}>Custom Splints</span>}
          />
        </div>
      </div>
    </div>
  );
};

ProductsListField.defaultProps = {};

ProductsListField.propTypes = {
  preferenceValue: PropTypes.number, // TODO(Marcin): Should be required!!!
  currentValue: PropTypes.number,
  currentImpression: PropTypes.number,
  setValue: PropTypes.func.isRequired
};

export default connect(({ formik: { values: encodeValues, setFieldValue }, ...props }) => {
  const values = decodeFormikValues(encodeValues);
  const currentValue = values[ATTRIBUTES.PRODUCTS_LIST_METHOD];
  const currentImpression = values[ATTRIBUTES.IMPRESSION_TYPE_METHOD];

  return (
    <ProductsListField
      setValue={value => setFieldValue(ATTRIBUTES.PRODUCTS_LIST_METHOD, String(value))}
      currentValue={currentValue}
      currentImpression={currentImpression}
      {...props}
    />
  );
});
